import type { fabric } from 'fabric';
import { getGuidePositions, isGuideObject } from '@/lib/guideLines';

const SNAP_MARGIN = 6;
const LINE_COLOR = 'rgba(13, 180, 168, 0.95)';
const LINE_WIDTH = 1;

/** When > 0, also snap to a regular grid while moving. */
let gridSnapSize = 0;
/** Smart snap to edges/centers/guides (independent of grid). */
let smartSnapEnabled = true;

export function setGridSnapSize(size: number): void {
  gridSnapSize = Math.max(0, Math.round(size));
}

export function getGridSnapSize(): number {
  return gridSnapSize;
}

export function setSmartSnapEnabled(enabled: boolean): void {
  smartSnapEnabled = enabled;
}

export function getSmartSnapEnabled(): boolean {
  return smartSnapEnabled;
}

interface GuideLine {
  x1: number;
  y1: number;
  x2: number;
  y2: number;
}

type CanvasWithTop = fabric.Canvas & {
  contextTop: CanvasRenderingContext2D;
  clearContext: (ctx: CanvasRenderingContext2D) => void;
};

/**
 * Smart guides: snap moving objects to artboard center and other object edges/centers.
 * Draws temporary lines on Fabric's top context.
 */
export function installSnapGuides(canvas: fabric.Canvas): () => void {
  const c = canvas as CanvasWithTop;
  let vertical: GuideLine[] = [];
  let horizontal: GuideLine[] = [];

  const drawLine = (ctx: CanvasRenderingContext2D, line: GuideLine) => {
    const vt = c.viewportTransform || [1, 0, 0, 1, 0, 0];
    ctx.save();
    ctx.strokeStyle = LINE_COLOR;
    ctx.lineWidth = LINE_WIDTH;
    ctx.setLineDash([4, 3]);
    ctx.beginPath();
    ctx.moveTo(line.x1 * vt[0] + vt[4], line.y1 * vt[3] + vt[5]);
    ctx.lineTo(line.x2 * vt[0] + vt[4], line.y2 * vt[3] + vt[5]);
    ctx.stroke();
    ctx.restore();
  };

  const clear = () => {
    vertical = [];
    horizontal = [];
  };

  const onBeforeRender = () => {
    if (c.contextTop) c.clearContext(c.contextTop);
  };

  const onAfterRender = () => {
    const ctx = c.contextTop;
    if (!ctx) return;
    vertical.forEach((l) => drawLine(ctx, l));
    horizontal.forEach((l) => drawLine(ctx, l));
  };

  const onMoving = (e: fabric.IEvent) => {
    const obj = e.target;
    if (!obj) return;
    if (isGuideObject(obj)) return;
    if (!smartSnapEnabled && gridSnapSize <= 0) {
      clear();
      return;
    }

    clear();
    obj.setCoords();

    const w = canvas.getWidth();
    const h = canvas.getHeight();
    const bound = obj.getBoundingRect(true);
    const objCenterX = bound.left + bound.width / 2;
    const objCenterY = bound.top + bound.height / 2;

    const snapTargetsV: { x: number; y1: number; y2: number }[] = [];
    const snapTargetsH: { y: number; x1: number; x2: number }[] = [];

    if (smartSnapEnabled) {
      snapTargetsV.push(
        { x: w / 2, y1: 0, y2: h },
        { x: 0, y1: 0, y2: h },
        { x: w, y1: 0, y2: h }
      );
      snapTargetsH.push(
        { y: h / 2, x1: 0, x2: w },
        { y: 0, x1: 0, x2: w },
        { y: h, x1: 0, x2: w }
      );

      const guides = getGuidePositions(canvas);
      guides.vertical.forEach((x) => {
        snapTargetsV.push({ x, y1: 0, y2: h });
      });
      guides.horizontal.forEach((y) => {
        snapTargetsH.push({ y, x1: 0, x2: w });
      });

      c.getObjects().forEach((other) => {
        if (other === obj || !other.visible || isGuideObject(other)) return;
        if (obj.type === 'activeSelection') {
          const sel = obj as fabric.ActiveSelection;
          if (sel.getObjects().includes(other)) return;
        }
        const b = other.getBoundingRect(true);
        snapTargetsV.push(
          {
            x: b.left,
            y1: Math.min(bound.top, b.top),
            y2: Math.max(bound.top + bound.height, b.top + b.height),
          },
          {
            x: b.left + b.width / 2,
            y1: Math.min(bound.top, b.top),
            y2: Math.max(bound.top + bound.height, b.top + b.height),
          },
          {
            x: b.left + b.width,
            y1: Math.min(bound.top, b.top),
            y2: Math.max(bound.top + bound.height, b.top + b.height),
          }
        );
        snapTargetsH.push(
          {
            y: b.top,
            x1: Math.min(bound.left, b.left),
            x2: Math.max(bound.left + bound.width, b.left + b.width),
          },
          {
            y: b.top + b.height / 2,
            x1: Math.min(bound.left, b.left),
            x2: Math.max(bound.left + bound.width, b.left + b.width),
          },
          {
            y: b.top + b.height,
            x1: Math.min(bound.left, b.left),
            x2: Math.max(bound.left + bound.width, b.left + b.width),
          }
        );
      });
    }

    if (gridSnapSize > 0) {
      for (let x = 0; x <= w; x += gridSnapSize) {
        snapTargetsV.push({ x, y1: 0, y2: h });
      }
      for (let y = 0; y <= h; y += gridSnapSize) {
        snapTargetsH.push({ y, x1: 0, x2: w });
      }
    }

    let dx = 0;
    let dy = 0;
    let snappedX = false;
    let snappedY = false;

    const objEdgesX = [bound.left, objCenterX, bound.left + bound.width];
    for (const target of snapTargetsV) {
      for (let i = 0; i < objEdgesX.length; i++) {
        const edge = objEdgesX[i];
        if (Math.abs(edge - target.x) <= SNAP_MARGIN) {
          dx = target.x - edge;
          vertical.push({
            x1: target.x,
            y1: Math.min(target.y1, bound.top + dy),
            x2: target.x,
            y2: Math.max(target.y2, bound.top + bound.height + dy),
          });
          snappedX = true;
          break;
        }
      }
      if (snappedX) break;
    }

    const objEdgesY = [bound.top, objCenterY, bound.top + bound.height];
    for (const target of snapTargetsH) {
      for (let i = 0; i < objEdgesY.length; i++) {
        const edge = objEdgesY[i];
        if (Math.abs(edge - target.y) <= SNAP_MARGIN) {
          dy = target.y - edge;
          horizontal.push({
            x1: Math.min(target.x1, bound.left + dx),
            y1: target.y,
            x2: Math.max(target.x2, bound.left + bound.width + dx),
            y2: target.y,
          });
          snappedY = true;
          break;
        }
      }
      if (snappedY) break;
    }

    if (dx !== 0 || dy !== 0) {
      obj.set({
        left: (obj.left || 0) + dx,
        top: (obj.top || 0) + dy,
      });
      obj.setCoords();
    }
  };

  const onModified = () => {
    clear();
    canvas.requestRenderAll();
  };

  canvas.on('before:render', onBeforeRender);
  canvas.on('after:render', onAfterRender);
  canvas.on('object:moving', onMoving);
  canvas.on('object:modified', onModified);
  canvas.on('mouse:up', onModified);

  return () => {
    canvas.off('before:render', onBeforeRender);
    canvas.off('after:render', onAfterRender);
    canvas.off('object:moving', onMoving);
    canvas.off('object:modified', onModified);
    canvas.off('mouse:up', onModified);
    clear();
  };
}
